import * as React from 'react'
import { useEffect, useState } from 'react'
import {
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  Card,
  Box,
} from '@mui/material'

import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

import Title from '../title/Title'
import { justifyContent } from '@mui/system'

const dollarFormat = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
})

// interface DataRow {
//   id: number
//   value: number
//   percent: number
//   title: string
//   color: any
// }

// interface TableListProps {
//   data: DataRow[]
//   selected: any
//   onSelectionChange: any
// }

// const TableList: React.FC<TableListProps> = ({
const TableList = ({
  data,
  selected,
  onSelectionChange,
  lifecycleFundBreakdowns,
  initialAllocation,
}) => {
  const [open, setOpen] = useState(null)

  // const handleRowClick = (index: number) => {
  const handleRowClick = (index) => {
    if (open === index) {
      setOpen(null)
      onSelectionChange(null)
    } else {
      setOpen(index)
      onSelectionChange({ type: 'pie', seriesId: 'A', dataIndex: index })
    }
  }

  const theme = useTheme()

  React.useEffect(() => {
    setOpen(selected?.dataIndex)
  }, [selected])

  const formatFundName = (key) =>
    key
      .replace('tsp', '')
      .replace('Fund', '')
      .replace(/([A-Z])/g, ' $1')
      .trim()

  const fundDetailsMap = {
    tspGFund: {
      label: 'G Fund',
      examples: 'U.S. Treasury securities',
      description:
        'Invests exclusively in short-term U.S. Treasury securities. Offers stable returns and protection against loss of principal, with interest rates similar to long-term government bonds.',
    },
    tspFFund: {
      label: 'F Fund',
      examples: 'Government, corporate, and mortgage-backed bonds',
      description:
        'Tracks the Bloomberg U.S. Aggregate Bond Index, covering a broad range of the U.S. bond market. As of December 2024, the portfolio includes roughly 48% government-related bonds, 24% credit, and 27% securitized debt. Provides income with moderate risk and lower volatility than stock funds.',
    },
    tspCFund: {
      label: 'C Fund',
      examples: 'Apple, Nvidia, Microsoft, Amazon, Meta, Tesla',
      description:
        'Provides exposure to large and mid-sized U.S. companies by tracking the S&P 500 Index. Investment in the C Fund offers the opportunity to participate in the growth of major corporations such as Apple, Nvidia, Microsoft, and Amazon.',
    },
    tspSFund: {
      label: 'S Fund',
      examples: 'Doordash, Coinbase, Snowflake, Block, Trade Desk',
      description:
        'Tracks the Dow Jones U.S. Completion Total Stock Market Index, which includes small- to mid-sized U.S. companies not found in the S&P 500. Offers high growth potential but with greater volatility.',
    },
    tspIFund: {
      label: 'I Fund',
      examples: 'Taiwan Semiconductor, ASML, Nestlé, Toyota, AstraZeneca',
      description:
        'Provides international diversification by investing in companies from more than 40 developed and emerging market countries (excluding the U.S.). Tracks a global index and includes large, medium, and small non-U.S. companies.',
    },

    tspLIncome: {
      label: 'L Income',
      examples: 'G, F, C, S, I',
      description:
        'Designed for participants who are withdrawing or about to begin withdrawing their TSP accounts. Offers low risk and prioritizes capital preservation and income.',
    },
    tspL2025: {
      label: 'L 2025',
      examples: 'G, F, C, S, I',
      description:
        'For participants born between 1960–1964 or planning to begin withdrawals between 2025–2027. Offers a low to medium risk profile with a gradually shifting asset mix.',
    },
    tspL2030: {
      label: 'L 2030',
      examples: 'G, F, C, S, I',
      description:
        'For participants born between 1965–1969 or planning to begin withdrawals between 2028–2032. Offers a medium risk profile with automatic quarterly adjustments.',
    },
    tspL2035: {
      label: 'L 2035',
      examples: 'G, F, C, S, I',
      description:
        'For participants planning to retire in the early-to-mid 2030s. Offers a moderate risk level and shifts gradually to preserve capital.',
    },
    tspL2040: {
      label: 'L 2040',
      examples: 'G, F, C, S, I',
      description:
        'For participants retiring around 2040. Designed for growth with a moderate to high risk profile that reduces over time.',
    },
    tspL2045: {
      label: 'L 2045',
      examples: 'G, F, C, S, I',
      description:
        'For participants targeting retirement between 2043–2047. Offers long-term growth with higher risk and daily rebalancing.',
    },
    tspL2050: {
      label: 'L 2050',
      examples: 'G, F, C, S, I',
      description:
        'For participants born between 1985–1989 or planning to begin withdrawals between 2048–2052. Designed for long-term growth with higher risk early on.',
    },
    tspL2055: {
      label: 'L 2055',
      examples: 'G, F, C, S, I',
      description:
        'For younger participants born after 1990 with a long investment horizon. High-growth, high-risk approach that becomes more conservative over time.',
    },
    tspL2060: {
      label: 'L 2060',
      examples: 'G, F, C, S, I',
      description:
        'Designed for those planning to withdraw funds in the early 2060s. Offers aggressive growth and long-term capital appreciation.',
    },
    tspL2065: {
      label: 'L 2065',
      examples: 'G, F, C, S, I',
      description:
        'For participants with a very long time horizon, such as those retiring after 2063. High-growth allocation with frequent rebalancing.',
    },
    tspL2070: {
      label: 'L 2070',
      examples: 'G, F, C, S, I',
      description:
        'Ideal for participants in their early careers or born after 2000. Maximizes growth potential with high equity exposure early on.',
    },
    tspL2075: {
      label: 'L 2075',
      examples: 'G, F, C, S, I',
      description:
        'Designed for the youngest investors or future generations. Targets maximum long-term growth with a glide path to safety.',
    },
    Unknown: {
      label: 'Unknown',
      examples: '',
      description:
        'This slice is unknown because it was left un-entered, ensure your allocations add up to 100% to remove this.',
    },
  }

  function getLabel(value) {
    if (value >= 0 && value <= 19) {
      return 'Very Low'
    } else if (value >= 20 && value <= 39) {
      return 'Low'
    } else if (value >= 40 && value <= 59) {
      return 'Moderate'
    } else if (value >= 60 && value <= 79) {
      return 'High'
    } else {
      return 'Very High'
    }
  }

  return (
    //for some reason im unable to give this any padding, so width for now
    <TableContainer sx={{ margin: 'auto', width: '100%' }}>
      <Table aria-label="collapsible table">
        <TableBody>
          {/* header */}
          <TableRow>
            <TableCell width={180} align="left">
              <Typography
                sx={{
                  paddingLeft: 3,
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#A3A7AA',
                }}
              >
                Items
              </Typography>
            </TableCell>

            <TableCell width={180} align="center">
              <Typography
                sx={{
                  // paddingRight: 5,
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#A3A7AA',
                }}
              >
                Percentage
              </Typography>
            </TableCell>
            <TableCell width={180} align="right">
              <Typography
                sx={{
                  paddingRight: 4,
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#A3A7AA',
                }}
              >
                Total Value
              </Typography>
            </TableCell>
          </TableRow>
          {/* items */}
          {data.map((row, index) => (
            <React.Fragment key={row.id}>
              <TableRow
                onClick={() => handleRowClick(index)}
                sx={{
                  //tried for a while to get hovering to work how it should, sibling css classes are as close as i could get
                  //grid might be a better solution for this
                  ':hover': {
                    backgroundColor: '#F1F1F1',
                    cursor: 'pointer',
                  },
                  '& > *': {
                    borderBottom: '0',
                  },
                  backgroundColor: open === index ? '#F1F1F1' : 'transparent',
                }}
              >
                <TableCell
                  width={180}
                  sx={{
                    paddingLeft: 5,
                    display: 'flex',
                    justifyContent: 'flex-start',
                    borderBottom: 0,
                  }}
                >
                  <Title highlighted={index} data={data} />
                </TableCell>

                <TableCell
                  width={180}
                  sx={{
                    flexDirection: 'row',
                    borderBottom: 0,
                  }}
                  align="center"
                >
                  <Typography
                    variant="body2"
                    align="center"
                    sx={{
                      fontSize: theme.typography.body2.fontSize,
                      fontWeight: theme.typography.body2.fontWeight,
                      color: '#495057',
                    }}
                  >
                    {row.percent.toFixed(2)}%
                  </Typography>
                </TableCell>

                <TableCell
                  width={180}
                  sx={{
                    flexDirection: 'row',
                    borderBottom: 0,
                    paddingRight: 5,
                  }}
                  align="right"
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: theme.typography.body2.fontSize,
                      fontWeight: theme.typography.body2.fontWeight,
                      color: '#495057',
                    }}
                  >
                    {dollarFormat.format(row.value)}
                    <span>
                      {open === index ? (
                        <KeyboardArrowUpIcon
                          sx={{ fontSize: '1.5rem', verticalAlign: 'middle' }}
                        />
                      ) : (
                        <KeyboardArrowDownIcon
                          sx={{ fontSize: '1.5rem', verticalAlign: 'middle' }}
                        />
                      )}
                    </span>
                  </Typography>
                </TableCell>
              </TableRow>
              {/* drop down */}
              <TableRow
                onClick={() => handleRowClick(index)}
                sx={{
                  backgroundColor: '#F1F1F1',
                }}
              >
                <TableCell
                  sx={{
                    paddingY: 0,
                    paddingLeft: 5,
                    paddingRight: 8,
                  }}
                  colSpan={3}
                >
                  <Collapse in={open === index} timeout="auto" unmountOnExit>
                    <TableContainer
                      sx={{
                        marginBottom: 2,
                        borderRadius: 2,
                        border: '1px solid #A3A7AA',
                      }}
                      component={Card}
                    >
                      <Table>
                        <TableBody
                          sx={{
                            backgroundColor:
                              open === index
                                ? theme.palette.common.white
                                : theme.palette.common.white,
                          }}
                        >
                          <TableRow
                            sx={{
                              verticalAlign: 'top',
                            }}
                          >
                            <TableCell>
                              <Typography
                                sx={{
                                  paddingRight: 2,
                                  fontSize: '16px',
                                  fontWeight: 600,
                                  color: '#A3A7AA',
                                }}
                              >
                                Risk Level
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                sx={{
                                  paddingRight: 2,
                                  fontSize: '16px',
                                  fontWeight: 600,
                                  color: '#495057',
                                }}
                              >
                                {row.risk.toFixed(0)} ({getLabel(row.risk)})
                              </Typography>
                            </TableCell>
                          </TableRow>

                          <TableRow
                            sx={{
                              verticalAlign: 'top',
                            }}
                          >
                            <TableCell>
                              <Typography
                                sx={{
                                  verticalAlign: 'top',
                                  paddingRight: 2,
                                  fontSize: '16px',
                                  fontWeight: 600,
                                  color: '#A3A7AA',
                                }}
                              >
                                Examples
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                sx={{
                                  paddingRight: 2,
                                  fontSize: '16px',
                                  fontWeight: 600,
                                  color: '#495057',
                                }}
                              >
                                {lifecycleFundBreakdowns[row.key] ? (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexWrap: 'wrap',
                                      gap: 1,
                                    }}
                                  >
                                    {Object.entries(
                                      lifecycleFundBreakdowns[row.key]
                                    ).map(([fundKey, percent]) => {
                                      const fundData =
                                        initialAllocation[fundKey]

                                      if (!fundData) return null

                                      return (
                                        <Box
                                          key={fundKey}
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                          }}
                                        >
                                          <Title
                                            highlighted={0}
                                            data={[
                                              {
                                                title: fundData.label,
                                                color: fundData.color,
                                              },
                                            ]}
                                          />
                                          <Typography
                                            sx={{
                                              marginLeft: 0.5,
                                              marginRight: 1,
                                              fontSize: '14px',
                                              color: '#495057',
                                              fontWeight: 500,
                                            }}
                                          >
                                            {percent.toFixed(2)}%
                                          </Typography>
                                        </Box>
                                      )
                                    })}
                                  </Box>
                                ) : (
                                  <Typography>
                                    {fundDetailsMap[row.key]?.examples || '—'}
                                  </Typography>
                                )}
                              </Typography>
                            </TableCell>
                          </TableRow>

                          <TableRow>
                            <TableCell
                              sx={{
                                verticalAlign: 'top',
                              }}
                            >
                              <Typography
                                sx={{
                                  paddingRight: 2,
                                  fontSize: '16px',
                                  fontWeight: 600,
                                  color: '#A3A7AA',
                                }}
                              >
                                Description
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                sx={{
                                  paddingRight: 2,
                                  fontSize: '16px',
                                  fontWeight: 600,
                                  color: '#495057',
                                }}
                              >
                                {fundDetailsMap[row.key]?.description || '—'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default TableList
